import useSWRImmutable from 'swr/immutable';
import settings from '/@/core/settings';
import { _ } from '/@/locale';
import { axFetch } from '/@/store/session/actions';
import { capitalizeFirstLetter } from '/@/utils';

type WeedOptions = {
    crop?: string;
    applicationMoment?: string;
    weedType?: number;
    weed?: number;
    weedSize?: number;
    type?: 'weeds';
};

type DiseaseOptions = {
    crop?: string;
    applicationType?: string;
    diseaseId?: number;
    type?: 'diseases';
};

type PlagueOptions = {
    crop?: string;
    applicationMoment?: string;
    applicationWindow?: string;
    actionThreshold?: string;
    plagueId?: number;
    type?: 'plagues';
};

type SeedTreatmentOptions = {
    crop?: string;
    type?: 'seed_treatment';
};

type CropProtectionOptions = WeedOptions | DiseaseOptions | PlagueOptions | SeedTreatmentOptions;

export type Translation = {
    es: string;
    en: string;
    pt: string;
};

type Crop = {
    id: number;
    name: Translation;
};

type Stage = {
    id: number;
    name: Translation;
    image: string;
    crop: Crop;
    order: number;
};

type CropProtectionProduct = {
    id: number;
    type: {
        id: number;
        name: Translation;
    };
    crop: Crop;
    name: Translation;
    image: string;
};

type SimplifiedUseRecommendation = {
    id: number;
    product: CropProtectionProduct;
    crop: Crop;
    fromStage: Stage;
    toStage: Stage;
    note: Translation;
    alert: Translation;
};

type APIResponse<T> = {
    count: number;
    results: T[];
};

type UseRecommendation = {
    id: number;
    product: CropProtectionProduct;
    crop?: Crop;
    fromStage?: Stage;
    toStage?: Stage;
    note: Translation;
    dose: Translation;
    whenToApply: Translation;
};

type UseRecommendationResponse = APIResponse<UseRecommendation>;
type SimplifiedUseRecommendationResponse = APIResponse<SimplifiedUseRecommendation>;

type CropStage = {
    id: number;
    name: Translation;
    image: string;
    crop: Crop;
    order: number;
};

type CropStageResponse = {
    count: number;
    results: CropStage[];
};

// Weed options

const getCPWeedOptionsFromApi = async ({ crop, applicationMoment, weedType, weed, weedSize }: WeedOptions) => {
    try {
        let url = `${settings.urls.apiDomain}/core/v1/crop-protection/weed-recommendation-options/?`;

        if (crop) {
            url += `&crop=${crop}`;
        }
        if (applicationMoment) {
            url += `&application_moment=${applicationMoment}`;
        }
        if (weedType) {
            url += `&weed_type=${weedType}`;
        }
        if (weed) {
            url += `&weed=${weed}`;
        }
        if (weedSize) {
            url += `&weed_size=${weedSize}`;
        }

        const res = await axFetch.get(url);
        return res.data.map((option) => {
            return {
                ...option,
                weed: option.weed
                    ? {
                          ...option.weed,
                          name: capitalizeFirstLetter(_(option.weed?.name)),
                      }
                    : null,
            };
        });
    } catch (error) {
        console.log(error);
        return [];
    }
};

// Disease options

const getCPDiseaseOptionsFromApi = async ({ crop, applicationType, diseaseId }: DiseaseOptions) => {
    try {
        let url = `${settings.urls.apiDomain}/core/v1/crop-protection/disease-recommendation-options/?`;

        if (crop) {
            url += `&crop=${crop}`;
        }
        if (applicationType) {
            url += `&applicationType=${applicationType}`;
        }
        if (diseaseId) {
            url += `&diseaseId=${diseaseId}`;
        }

        const res = await axFetch.get(url);
        return res.data;
    } catch (error) {
        return [];
    }
};

// Plague options

const getCPPlagueOptionsFromApi = async ({
    crop,
    applicationMoment,
    applicationWindow,
    actionThreshold,
    plagueId,
}: PlagueOptions) => {
    try {
        let url = `${settings.urls.apiDomain}/core/v1/crop-protection/plague-recommendation-options/?`;

        if (crop) {
            url += `&crop=${crop}`;
        }
        if (applicationMoment) {
            url += `&applicationMoment=${applicationMoment}`;
        }
        if (applicationWindow) {
            url += `&applicationWindow=${applicationWindow}`;
        }
        if (actionThreshold) {
            url += `&actionThreshold=${actionThreshold}`;
        }
        if (plagueId) {
            url += `&plagueId=${plagueId}`;
        }

        const res = await axFetch.get(url);
        return res.data;
    } catch (error) {
        return [];
    }
};

const getCPSeedTreatmentOptionsFromApi = async ({ crop }: SeedTreatmentOptions) => {
    try {
        let url = `${settings.urls.apiDomain}/core/v1/crop-protection/seed-treatment-recommendation-options/?`;

        if (crop) {
            url += `&crop=${crop}`;
        }

        const res = await axFetch.get(url);
        return res.data.map((option) => {
            return {
                ...option,
                weed: option.weed
                    ? {
                          ...option.weed,
                          name: capitalizeFirstLetter(_(option.weed?.name)),
                      }
                    : null,
            };
        });
    } catch (error) {
        return [];
    }
};

export const useCropProtectionOptions = (props: CropProtectionOptions) => {
    let key: string | null = `cp-options-${props.crop}-${props.type}`;
    let fetchMethod: (() => Promise<any>) | undefined;

    if (!props.crop) key = null;

    switch (props.type) {
        case 'weeds':
            if (props.applicationMoment) key += `-${props.applicationMoment}`;
            if (props.weedType) key += `-${props.weedType}`;
            if (props.weed) key += `-${props.weed}`;
            if (props.weedSize) key += `-${props.weedSize}`;

            fetchMethod = () =>
                getCPWeedOptionsFromApi({
                    crop: props.crop,
                    applicationMoment: props.applicationMoment,
                    weedType: props.weedType,
                    weed: props.weed,
                    weedSize: props.weedSize,
                });
            break;
        case 'diseases':
            if (props.applicationType) key += `-${props.applicationType}`;
            if (props.diseaseId) key += `-${props.diseaseId}`;

            fetchMethod = () =>
                getCPDiseaseOptionsFromApi({
                    crop: props.crop,
                    applicationType: props.applicationType,
                    diseaseId: props.diseaseId,
                });
            break;
        case 'plagues':
            if (props.applicationMoment) key += `-${props.applicationMoment}`;
            if (props.applicationWindow) key += `-${props.applicationWindow}`;
            if (props.actionThreshold) key += `-${props.actionThreshold}`;
            if (props.plagueId) key += `-${props.plagueId}`;
            fetchMethod = () =>
                getCPPlagueOptionsFromApi({
                    crop: props.crop,
                    applicationMoment: props.applicationMoment,
                    applicationWindow: props.applicationWindow,
                    actionThreshold: props.actionThreshold,
                    plagueId: props.plagueId,
                });
            break;
        case 'seed_treatment':
            if (props.applicationMoment) key += `-${props.applicationMoment}`;
            if (props.weedType) key += `-${props.weedType}`;
            if (props.weed) key += `-${props.weed}`;
            if (props.weedSize) key += `-${props.weedSize}`;
            fetchMethod = () =>
                getCPSeedTreatmentOptionsFromApi({
                    crop: props.crop,
                    applicationMoment: props.applicationMoment,
                    weedType: props.weedType,
                    weed: props.weed,
                    weedSize: props.weedSize,
                });
            break;
    }

    return useSWRImmutable(key, fetchMethod ? fetchMethod : null);
};

// diseases list

const getCPDiseaseListFromApi = async () => {
    try {
        const url = `${settings.urls.apiDomain}/core/v1/crop-protection/diseases`;
        const res = await axFetch.get(url);
        return res.data;
    } catch (error) {
        return [];
    }
};

export const useCropProtectionDiseaseList = () => {
    const key: string = 'cp-disease-list';
    return useSWRImmutable(key, () => getCPDiseaseListFromApi());
};

// plagues list

const getCPPlagueListFromApi = async () => {
    try {
        const url = settings.urls.apiDomain + '/core/v1/crop-protection/plagues';
        const res = await axFetch.get(url);
        return res.data;
    } catch (error) {
        return [];
    }
};

export const useCropProtectionPlagueList = () => {
    const key: string = 'cp-plague-list';
    return useSWRImmutable(key, () => getCPPlagueListFromApi());
};

// simplified use recommendations

const getCPSimplifiedUseRecommendationsFromApi = async ({
    cropId,
    productId,
    cropName,
    crops,
    page,
    pageSize = 10,
    brandIds,
}: {
    cropId?: number;
    productId?: number;
    cropName?: string;
    crops?: string[];
    page: number;
    pageSize?: number;
    brandIds?: number[];
}) => {
    let url = `${settings.urls.apiDomain}/core/v1/crop-protection/simplified-use-recommendations/?`;
    if (cropId) url += `&crop_id=${cropId}`;
    if (cropName) url += `&crop__name_en=${cropName}`;
    if (productId) url += `&product_id=${productId}`;
    if (page) url += `&page=${page}`;
    if (pageSize) url += `&page_size=${pageSize}`;
    if (brandIds) url += `&product__brand__id__in=${brandIds.join(',')}`;
    if (crops) url += `&crop_id__in=${crops.join(',')}`;
    const res = await axFetch.get<SimplifiedUseRecommendationResponse>(url);
    return res.data;
};

export const useCPSimplifiedUseRecommendations = (
    {
        cropId,
        productId,
        cropName,
        brandIds,
        crops,
        page,
        pageSize = 10,
    }: {
        cropId?: number;
        productId?: number;
        cropName?: string;
        brandIds?: number[];
        crops?: string[];
        page: number;
        pageSize?: number;
    },
    { enabled }: { enabled: boolean } = { enabled: true }
) => {
    const key = {
        key: 'useCPSimplifiedUseRecommendations',
        cropId,
        productId,
        cropName,
        brandIds,
        crops,
        page,
        pageSize,
    };
    return useSWRImmutable(enabled ? key : null, (key) => getCPSimplifiedUseRecommendationsFromApi(key));
};

// use recommendations

const getCPUseRecommendationsFromApi = async ({
    cropId,
    productId,
    productIdIn,
    cropName,
    crops,
    page,
    pageSize = 10,
    brandIds,
}: {
    cropId?: number;
    productId?: number;
    productIdIn?: number[];
    cropName?: string;
    crops?: string[];
    page: number;
    pageSize?: number;
    brandIds?: number[];
}) => {
    let url = `${settings.urls.apiDomain}/core/v1/crop-protection/use-recommendations/?`;
    if (cropId) url += `&crop_id=${cropId}`;
    if (cropName) url += `&crop__name_en=${cropName}`;
    if (productId) url += `&product_id=${productId}`;
    if (productIdIn) url += `&product_id__in=${productIdIn.join(',')}`;
    if (page) url += `&page=${page}`;
    if (pageSize) url += `&page_size=${pageSize}`;
    if (brandIds) url += `&product__brand__id__in=${brandIds.join(',')}`;
    if (crops) url += `&crop_id__in=${crops.join(',')}`;
    const res = await axFetch.get<UseRecommendationResponse>(url);
    return res.data;
};

export const useCPUseRecommendations = (
    {
        cropId,
        productId,
        productIdIn,
        cropName,
        brandIds,
        crops,
        page,
        pageSize = 10,
    }: {
        cropId?: number;
        productId?: number;
        productIdIn?: number[];
        cropName?: string;
        brandIds?: number[];
        crops?: string[];
        page: number;
        pageSize?: number;
    },
    { enabled = true }: { enabled?: boolean } = { enabled: true }
) => {
    const key = {
        key: 'useCPUseRecommendations',
        cropId,
        productId,
        productIdIn,
        cropName,
        brandIds,
        crops,
        page,
        pageSize,
    };
    return useSWRImmutable(enabled ? key : null, (key) => getCPUseRecommendationsFromApi(key));
};

// crop stages

const getCPCropStagesFromApi = async ({ page, pageSize, crop }: { page: number; pageSize?: number; crop?: string }) => {
    let url = `${settings.urls.apiDomain}/core/v1/crop-protection/crop-stages/?`;
    if (page) url += `&page=${page}`;
    if (pageSize) url += `&page_size=${pageSize}`;
    if (crop) url += `&crop__name_en__icontains=${crop}`;
    const res = await axFetch.get<CropStageResponse>(url);
    return res.data;
};

export const useCPCropStages = ({ page, pageSize, crop }: { page: number; pageSize?: number; crop?: string }) => {
    const key = {
        key: 'useCPCropStages',
        page,
        crop,
        pageSize,
    };
    return useSWRImmutable(key, (key) => getCPCropStagesFromApi(key));
};
